import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/app/api";
import { useAuth } from "@/app/AuthContext";

const STATUS_STYLE = {
  potential: "bg-slate-100 text-slate-600", pending: "bg-amber-50 text-amber-700",
  confirmed: "bg-sky-50 text-sky-700", approved: "bg-indigo-50 text-indigo-700",
  payable: "bg-cobalt/10 text-cobalt", paid: "bg-emerald-50 text-emerald-700",
  excluded: "bg-rose-50 text-rose-700", void: "bg-slate-100 text-slate-400",
};
const STATUSES = ["", "potential", "pending", "confirmed", "approved", "payable", "paid", "excluded", "void"];
const SYMBOLS = { GBP: "£", EUR: "€", USD: "$" };
export const money = (amount, currency) => {
  if (amount == null) return "—";
  const code = currency || "GBP";
  return `${SYMBOLS[code] || `${code} `}${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function CommissionList() {
  const { user } = useAuth();
  const base = user.role === "admin" ? "/app/admin" : "/app/partner";
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("");
  const [demoView, setDemoView] = useState("all");

  useEffect(() => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (demoView !== "all") params.set("demo", demoView === "test" ? "true" : "false");
    const qs = params.toString() ? `?${params}` : "";
    api.get(`/commissions${qs}`).then((r) => setData(r.data.commissions)).catch(() => setData([]));
    api.get(`/commissions/summary`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [status, demoView]);

  const cards = summary ? [
    ["Entitled (live)", money(summary.live.entitled, "GBP")], ["Paid (live)", money(summary.live.paid, "GBP")],
    ["Outstanding (live)", money(summary.live.outstanding, "GBP")],
    ["TEST entitled", money(summary.test.entitled, "GBP")],
  ] : [];

  return (
    <div data-testid="commission-list" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-ink">Commissions</h1>
        <p className="mt-1 text-sm text-slatey">Commercial records from successful applications — calculated from each partner's configuration. TEST records are always separated from live commercial activity.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        {cards.map(([label, value]) => (
          <div key={label} className="rounded-3xl border border-slate-200 bg-white p-4" data-testid={`summary-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-xs font-bold uppercase text-slatey">{label}</p>
            <p className="mt-1 font-display text-xl font-bold text-ink">{value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((value) => (
          <button key={value || "all"} onClick={() => setStatus(value)} data-testid={`commission-filter-${value || "all"}`}
            className={`min-w-max rounded-full px-4 py-2 text-xs font-bold capitalize ${status === value ? "bg-cobalt text-white" : "bg-white text-slatey border border-slate-200"}`}>
            {value || "All"}
          </button>
        ))}
        <span className="mx-1 border-l border-slate-200" />
        {[["all", "All records"], ["live", "Live only"], ["test", "TEST only"]].map(([value, label]) => (
          <button key={value} onClick={() => setDemoView(value)} data-testid={`commission-demo-${value}`}
            className={`min-w-max rounded-full px-4 py-2 text-xs font-bold ${demoView === value ? "bg-ink text-white" : "bg-white text-slatey border border-slate-200"}`}>{label}</button>
        ))}
      </div>

      {!data ? <p className="text-sm text-slatey">Loading…</p> : (
        <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead><tr className="border-b border-slate-100 text-xs font-bold uppercase text-slatey">
              <th className="px-5 py-3">Learner</th><th className="px-5 py-3">University / Intake</th><th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Total</th><th className="px-5 py-3 text-right">Paid</th><th className="px-5 py-3 text-right">Outstanding</th>
            </tr></thead>
            <tbody>
              {data.map((record, index) => (
                <tr key={record.id || index} className="border-b border-slate-50 hover:bg-surface">
                  <td className="px-5 py-3">
                    <Link to={`${base}/commissions/${record.id}`} data-testid={`commission-${record.id}`} className="font-bold text-cobalt hover:underline">
                      {record.learner_name || "Learner"}
                    </Link>
                    <p className="text-xs text-slatey">{record.application_reference} {record.is_demo && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">TEST</span>}</p>
                  </td>
                  <td className="px-5 py-3 text-slatey">{record.provider_name}<p className="text-xs">{record.intake_label}</p></td>
                  <td className="px-5 py-3"><span className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLE[record.status] || STATUS_STYLE.potential}`} data-testid={`commission-status-${record.id}`}>{record.status}</span></td>
                  <td className="px-5 py-3 text-right font-semibold text-ink">{money(record.total, record.currency)}</td>
                  <td className="px-5 py-3 text-right text-slatey">{money(record.paid_amount, record.currency)}</td>
                  <td className="px-5 py-3 text-right text-slatey">{record.total != null ? money(record.total - (record.paid_amount || 0), record.currency) : "—"}</td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-sm text-slatey">No commission records match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
